import { useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useModalConfirmFieldChange } from "../modals/ModalConfirmFieldChange";
import { IFormSchemaField } from "../types";

/**
 * Implements the `ui.confirm` contract for a field: when its value changes (to
 * `confirm.whenEquals` when set — otherwise any change), open a confirmation
 * modal; on cancel/dismiss the change is reverted to the previous value.
 *
 * The value technically commits to RHF first and is rolled back on cancel — the
 * existing field components own their onChange wiring (WSForm.Field), so there
 * is no pre-commit interception point. The net result is identical: a cancelled
 * change never survives (debounced draft/re-evaluation both settle on the
 * reverted value). The initial value never prompts.
 */
export const useConfirmFieldChange = (
  field: IFormSchemaField,
  name: string
) => {
  const confirm = field.ui?.confirm;
  const { control, setValue } = useFormContext();
  const value = useWatch({ control, name });
  const modal = useModalConfirmFieldChange();

  // Last user-confirmed (or initial) value — the revert target.
  const confirmedValueRef = useRef<unknown>(value);
  // Re-entrancy guard: value flaps while the modal is open are ignored.
  const isPromptingRef = useRef(false);
  // The modal hook returns a fresh `open` each render; keep the latest in a ref
  // so the effect below depends only on the watched value.
  const openModalRef = useRef(modal.open);
  openModalRef.current = modal.open;

  useEffect(() => {
    if (!confirm) return;
    const previous = confirmedValueRef.current;
    if (value === previous || isPromptingRef.current) return;

    const requiresConfirmation =
      confirm.whenEquals === undefined || value === confirm.whenEquals;
    if (!requiresConfirmation) {
      confirmedValueRef.current = value;
      return;
    }

    isPromptingRef.current = true;
    (async () => {
      const confirmed = await openModalRef.current({ confirm });
      if (confirmed) {
        confirmedValueRef.current = value;
      } else {
        setValue(name, previous, { shouldValidate: true, shouldDirty: true });
      }
      isPromptingRef.current = false;
    })();
    // Only the committed value drives the prompt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
};
